<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { createFrontendVersionChecker } from '@/utils/frontendVersion.js'

// Vite's `define` substitutes __PRUFA_FRONTEND_BUILD_ID__ at parse time
// with the same buildId that the inline plugin wrote to
// dist/frontend-version.json.
const checker = createFrontendVersionChecker({
  currentBuildId: __PRUFA_FRONTEND_BUILD_ID__,
  enabled: import.meta.env.PROD,
})

const visible = ref(false)
const latestBuildId = ref('')
let stopChecking

onMounted(() => {
  stopChecking = checker.start((update) => {
    latestBuildId.value = update.latestBuildId
    visible.value = true
  })
})

onUnmounted(() => {
  stopChecking?.()
})

function refresh() {
  window.location.reload()
}

function snooze() {
  checker.snooze(latestBuildId.value)
  visible.value = false
}
</script>

<template>
  <Teleport to="body">
    <Transition name="frontend-update">
      <aside
        v-if="visible"
        class="frontend-update-shell"
        role="region"
        aria-label="Frontend update available"
        aria-live="polite"
      >
        <div class="frontend-update-card">
          <div class="frontend-update-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12a9 9 0 1 1-3-6.7" />
              <path d="M21 4v5h-5" />
            </svg>
          </div>

          <div class="frontend-update-copy">
            <p class="frontend-update-title">A new Prufa version is ready</p>
            <p class="frontend-update-message">Refresh to load the latest UI.</p>
          </div>

          <div class="frontend-update-actions">
            <button type="button" class="frontend-update-secondary" @click="snooze">
              Later
            </button>
            <button type="button" class="frontend-update-primary" @click="refresh">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-3-6.7" />
                <path d="M21 4v5h-5" />
              </svg>
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
.frontend-update-shell {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 12px);
  left: 12px;
  right: 12px;
  z-index: 70;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.frontend-update-card {
  width: min(100%, 560px);
  pointer-events: auto;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--green) 14%, transparent), transparent 36%),
    var(--surface);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
}

.frontend-update-icon {
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: var(--green);
  background: var(--bg);
  border: 1px solid var(--border);
}

.frontend-update-copy {
  min-width: 0;
}

.frontend-update-title {
  margin: 0;
  color: var(--ink);
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.25;
}

.frontend-update-message {
  margin: 3px 0 0;
  color: var(--muted);
  font-size: 0.82rem;
  line-height: 1.35;
}

.frontend-update-actions {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.frontend-update-primary,
.frontend-update-secondary {
  min-height: 38px;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.frontend-update-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: white;
  background: var(--green);
  border: 1px solid var(--green);
}

.frontend-update-primary:hover {
  filter: brightness(0.95);
}

.frontend-update-secondary {
  color: var(--ink);
  background: var(--bg);
  border: 1px solid var(--border);
}

.frontend-update-secondary:hover {
  background: var(--surface);
}

.frontend-update-primary:active,
.frontend-update-secondary:active {
  transform: scale(0.98);
}

.frontend-update-enter-active,
.frontend-update-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.frontend-update-enter-from,
.frontend-update-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (min-width: 768px) {
  .frontend-update-shell {
    left: auto;
    right: 24px;
    justify-content: flex-end;
  }

  .frontend-update-card {
    width: 430px;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
  }

  .frontend-update-actions {
    grid-column: auto;
    grid-template-columns: auto auto;
    align-items: center;
  }

  .frontend-update-primary,
  .frontend-update-secondary {
    min-height: 34px;
    padding: 0 12px;
  }
}
</style>
